import { useSetAtom } from 'jotai';
import { useRouter } from '@/hooks';
import { cameraTarget, selectedAssemblyId } from '@/components/Analysis/state';
import { useCallback } from 'react';

interface ViewIn3DParams {
  assemblyId: string | undefined;
  isUncontextualized: boolean;
  centerPoint3d: string | null;
  imageLink: string;
  fov: number;
}

/**
 * A reusable hook for viewing POIs in 3D
 *
 * @param params - The parameters needed for viewing in 3D
 * @returns An object with the handleClick function that can be attached to a button
 */
export const useViewPoiIn3D = (params?: ViewIn3DParams) => {
  const setSelectedAssemblyId = useSetAtom(selectedAssemblyId);
  const setCameraTarget = useSetAtom(cameraTarget);
  const router = useRouter();

  /**
   * Creates a click handler for viewing a POI in 3D
   *
   * @param viewParams - The parameters needed for viewing in 3D
   * @returns A click handler function
   */
  const createClickHandler = useCallback(
    (viewParams: ViewIn3DParams) => (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      const { assemblyId, isUncontextualized, centerPoint3d, imageLink, fov } = viewParams;

      if (!isUncontextualized && centerPoint3d) {
        const lookAt = JSON.parse(centerPoint3d);
        setCameraTarget({ lookAt, fov });
      }

      setSelectedAssemblyId(assemblyId);
      const isCtrlClick = event.ctrlKey || event.metaKey;
      if (isCtrlClick) window.open(imageLink, '_blank');
      else router.push(imageLink.replace(window.location.origin, ''));
    },
    [setCameraTarget, setSelectedAssemblyId, router]
  );

  // If params are provided directly to the hook, create a handler for those params
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (params) {
        createClickHandler(params)(event);
      }
    },
    [params, createClickHandler]
  );

  return {
    handleClick,
    createClickHandler,
  };
};
